import { observable, action, toJS } from 'mobx';
import { Entity, AccelFile, VideoStream, VideoAccess, Updatable } from '..';
import { Lang } from '../../enums';
import { ISerializable } from '../Entity';

export type VideoBreadcrumbItem = {
    id: string;
    parentId: string;
    name: string;
    softDeleted: boolean;
}

export type WatermarkSettings = {
    enabled: boolean;
    frequencyDelayInSec: number;
}

export type VideoPlaybackSettings = {
    forwardSeekDisabled: boolean;
    miniPlayerEnabled: boolean;
}

export type VideoAnalyticsSettings = {
    metrika: boolean;
    metrikaCounterId: string;
    googleAnalytics: boolean;
    googleAnalyticsMeasurementId: string;
    meta: boolean;
    metaPixelId: string;
    eventPrefix: string;
}

export type VideoChapter = {
    startSec: number;
    title: string;
}

export type VideoChapterSet = {
    id: string;
    name: string;
    chapters: VideoChapter[];
}

export const videoChapterLimits = {
    sets: 10,
    chapters: 200,
    titleLength: 120
};

const normalizeChapterSet = (set: VideoChapterSet): VideoChapterSet => {
    const seen: number[] = [];
    const chapters = set.chapters
        .map(chapter => ({
            startSec: Math.max(0, Math.round(chapter.startSec || 0)),
            title: (chapter.title ?? '').trim().substring(0, videoChapterLimits.titleLength)
        }))
        .filter(chapter => {
            if (!chapter.title || seen.indexOf(chapter.startSec) != -1)
                return false;

            seen.push(chapter.startSec);

            return true;
        })
        .sort((left, right) => left.startSec - right.startSec);

    return {
        ...set,
        name: (set.name ?? '').trim().substring(0, videoChapterLimits.titleLength),
        chapters: chapters
    };
}

const hasChapterSetsChanges = (left: VideoChapterSet[] = [], right: VideoChapterSet[] = []) => {
    if (left.length != right.length)
        return true;

    return left.some((set, index) => {
        const other = right[index];

        return set.id != other.id
            || set.name != other.name
            || set.chapters.length != other.chapters.length
            || set.chapters.some((chapter, chapterIndex) =>
                chapter.startSec != other.chapters[chapterIndex].startSec
                || chapter.title != other.chapters[chapterIndex].title);
    });
}

export const hasAnalyticsTarget = (analytics?: VideoAnalyticsSettings) => {
    return analytics?.metrika == true || analytics?.googleAnalytics == true || analytics?.meta == true;
}

/**
 * Video settings
 */
export class VideoSettings extends Updatable implements ISerializable {

    constructor(settings?: Partial<VideoSettings>) {
        super();
        if (settings) this.update(settings);
    }


    @observable watermark: WatermarkSettings;
    @action updateWatermark(watermark: Partial<WatermarkSettings>) {
        this.watermark = { ...this.watermark, ...watermark };
    }

    @observable playback: VideoPlaybackSettings;
    @action updatePlayback(playback: Partial<VideoPlaybackSettings>) {
        this.playback = { ...this.playback, ...playback };
    }

    @observable analytics: VideoAnalyticsSettings;
    @action updateAnalytics(analytics: Partial<VideoAnalyticsSettings>) {
        this.analytics = { ...this.analytics, ...analytics };
    }

    @observable chapterSets: VideoChapterSet[] = [];
    @action updateChapterSets(chapterSets: VideoChapterSet[]) {
        this.chapterSets = chapterSets;
    }

    @action normalizeChapterSets() {
        this.chapterSets = (this.chapterSets ?? []).map(normalizeChapterSet);
    }

    @action update(changes: Partial<VideoSettings>, allowUndefined = false) {
        super.update(changes, allowUndefined);
    }

    toJson() {
        return toJS({
            watermark: this.watermark,
            playback: this.playback,
            analytics: this.analytics,
            chapterSets: this.chapterSets
        });
    }

    clone(): VideoSettings {
        return new VideoSettings({
            ...this,
        });
    }

    hasChanges(settings: VideoSettings): boolean {
        return this.watermark?.enabled != settings.watermark?.enabled
            || this.watermark?.frequencyDelayInSec != settings.watermark?.frequencyDelayInSec
            || this.playback?.forwardSeekDisabled != settings.playback?.forwardSeekDisabled
            || this.playback?.miniPlayerEnabled != settings.playback?.miniPlayerEnabled
            || this.analytics?.metrika != settings.analytics?.metrika
            || this.analytics?.googleAnalytics != settings.analytics?.googleAnalytics
            || this.analytics?.googleAnalyticsMeasurementId != settings.analytics?.googleAnalyticsMeasurementId
            || this.analytics?.meta != settings.analytics?.meta
            || this.analytics?.metaPixelId != settings.analytics?.metaPixelId
            || this.analytics?.eventPrefix != settings.analytics?.eventPrefix
            || this.analytics?.metrikaCounterId != settings.analytics?.metrikaCounterId
            || hasChapterSetsChanges(this.chapterSets, settings.chapterSets);
    }

    static default(): VideoSettings {
        return new VideoSettings({
            watermark: { enabled: false, frequencyDelayInSec: 30 },
            playback: { forwardSeekDisabled: false, miniPlayerEnabled: true },
            analytics: {
                metrika: false, metrikaCounterId: '',
                googleAnalytics: false, googleAnalyticsMeasurementId: '',
                meta: false, metaPixelId: '',
                eventPrefix: ''
            },
            chapterSets: []
        });
    }

    static fromJson(json: any): VideoSettings {
        const def = VideoSettings.default();
        return new VideoSettings({
            watermark: json.watermark || def.watermark,
            playback: json.playback || def.playback,
            analytics: json.analytics || def.analytics,
            chapterSets: json.chapterSets || []
        });
    }
}
export default class Video extends Entity {
    constructor(video?: Partial<Video>) {
        super(video);
        if (video) this.update(video);
    }

    @observable name: string;
    @observable shortDescription: string;
    @observable lang: Lang;
    @observable viewsCount: number;

    @observable createdDate: Date;
    @observable updatedDate: Date;
    @observable isNotCompletedStreamExist: boolean;

    @observable currentVideoStream: VideoStream;
    @observable primaryImage: AccelFile;
    @observable primaryImageFileId: string;

    @observable accessAnywhere: boolean;
    @observable access: VideoAccess | null;

    @observable breadcrumbs: VideoBreadcrumbItem[];
    @observable settings: VideoSettings;

    @action
    update(changes: Partial<Video>, allowUndefined = false) {
        super.update(changes, allowUndefined);
    }

    clone(): Video {
        return new Video({
            ...this,
            currentVideoStream: this.currentVideoStream?.clone(),
            primaryImage: this.primaryImage?.clone(),
            settings: this.settings?.clone()
        });
    }

    hasChanges(video: Video) {
        return this.name !== video.name
            || this.shortDescription !== video.shortDescription
            || this.lang !== video.lang
            || this.settings.hasChanges(video.settings);
    }


    static fromJson(json: any): Video {
        return new Video({
            ...json,
            createdDate: json.createdDate ? new Date(json.createdDate) : undefined,
            updatedDate: json.updatedDate ? new Date(json.updatedDate) : undefined,
            currentVideoStream: json.currentVideoStream ? VideoStream.fromJson(json.currentVideoStream) : undefined,
            primaryImage: json.primaryImage?.file ? AccelFile.fromJson(json.primaryImage.file) : undefined,
            primaryImageFileId: json.primaryImage?.file?.id ? json.primaryImage.file.id : undefined,
            access: json.videoAccess ? VideoAccess.fromJson(json.videoAccess) : null,
            breadcrumbs: json.path,
            settings: json.settingsJson
                ? VideoSettings.fromJson(JSON.parse(json.settingsJson))
                : VideoSettings.default()
        });
    }
}

